/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Plus, X, Search, ChevronRight, Bell, Users,
  User, CheckCheck, Clock, Loader2, 
  Paperclip, Send, Filter, Eye, MessageSquare, Check,
  ChevronDown, BookOpen, Megaphone, Info, AlertTriangle,
  CalendarDays, Globe, Building, RefreshCw,
} from 'lucide-react';
import Page from '../../../components/Page';
import DialogModal from '../../../components/modal/DialogModal';
import Modal from '../../../components/modal/Index';
import { getDepartments, getTeams, getTeamMembers } from '../../../services/clientAdmin/departmentServices';
import adminServices from '../../../services/adminServices';
import tenantUserServices from '../../../services/clientAdmin/tenantUserServices';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d) {
  if (!d) return '—';
  const date = new Date(d);
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtFull(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TYPE_CONFIG = {
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700', icon: AlertTriangle, iconColor: 'text-red-500' },
  info: { label: 'Info', color: 'bg-blue-100 text-blue-700', icon: Info, iconColor: 'text-blue-500' },
  general: { label: 'General', color: 'bg-gray-100 text-gray-600', icon: Megaphone, iconColor: 'text-gray-500' },
};

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  normal: { label: 'Normal', color: 'bg-green-100 text-green-700' },
};

function TypeBadge({ type }) {
  const c = TYPE_CONFIG[type] || TYPE_CONFIG.general;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.color}`}>{c.label}</span>;
}

function PriorityBadge({ priority }) {
  const c = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.color}`}>{c.label}</span>;
}

function AudienceBadge({ audiences }) {
  if (!audiences || !audiences.length) return null;
  const type = audiences[0]?.audienceType || 'all';
  const icons = { all: Globe, department: Building, team: Users, individual: User };
  const labels = { all: 'All Users', department: 'Department', team: 'Team', individual: 'Individual' };
  const Icon = icons[type] || Globe;
  return (
    <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
      <Icon size={10} /> {labels[type]}
    </span>
  );
}

function StatPill({ icon: Icon, value, label, color }) {
  return (
    <div className={`flex flex-col items-center px-4 py-2 rounded-lg ${color}`}>
      <Icon size={14} className="mb-1 opacity-70" />
      <span className="text-lg font-semibold leading-none">{value}</span>
      <span className="text-xs mt-0.5 opacity-70">{label}</span>
    </div>
  );
}

// ─── Simple Rich Text Toolbar ─────────────────────────────────────────────────

function RichEditor({ value, onChange }) {
  const editorRef = useRef(null);

  function exec(cmd, val = null) {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    onChange(editorRef.current?.innerHTML || '');
  }

  function handleInput() {
    onChange(editorRef.current?.innerHTML || '');
  }

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, []);

  const toolbarBtn = (cmd, icon, title) => (
    <button type="button" title={title} onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-600 text-sm font-medium">
      {icon}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-purple-400 focus-within:border-purple-400">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
        {toolbarBtn('bold', <strong>B</strong>, 'Bold')}
        {toolbarBtn('italic', <em>I</em>, 'Italic')}
        {toolbarBtn('underline', <span style={{ textDecoration: 'underline' }}>U</span>, 'Underline')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {toolbarBtn('insertUnorderedList', '• List', 'Bullet list')}
        {toolbarBtn('insertOrderedList', '1. List', 'Numbered list')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {toolbarBtn('justifyLeft', '⬅', 'Align left')}
        {toolbarBtn('justifyCenter', '↔', 'Center')}
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[140px] max-h-[260px] overflow-y-auto px-3 py-2 text-sm text-gray-800 focus:outline-none"
        style={{ lineHeight: '1.6' }}
        suppressContentEditableWarning
      />
    </div>
  );
}

// ─── Compose Modal ────────────────────────────────────────────────────────────

function ComposeModal({ onClose, onSend, departments, teams, users, loadTeams }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    title: '', body: '', type: 'general', priority: 'normal',
    requiresAck: false, allowReplies: false, attachment: null,
  });
  const [audience, setAudience] = useState({ type: 'all', depts: [], teams: [], users: [] });
  const [audienceTab, setAudienceTab] = useState('all');
  const [expandedDept, setExpandedDept] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  function toggleDept(id) {
    setAudience(prev => ({
      ...prev,
      depts: prev.depts.includes(id) ? prev.depts.filter(x => x !== id) : [...prev.depts, id],
    }));
  }

  function toggleTeam(id) {
    setAudience(prev => ({
      ...prev,
      teams: prev.teams.includes(id) ? prev.teams.filter(x => x !== id) : [...prev.teams, id],
    }));
  }

  function toggleUser(id) {
    setAudience(prev => ({
      ...prev,
      users: prev.users.includes(id) ? prev.users.filter(x => x !== id) : [...prev.users, id],
    }));
  }

  function selectAllDepts() {
    const all = departments.map(d => d.id);
    setAudience(prev => ({ ...prev, depts: prev.depts.length === all.length ? [] : all }));
  }

  function selectAllTeams() {
    const allTeams = Object.values(teams).flat().map(t => t.id);
    setAudience(prev => ({ ...prev, teams: prev.teams.length === allTeams.length ? [] : allTeams }));
  }

  function selectAllUsers() {
    const all = users.map(u => u.id);
    setAudience(prev => ({ ...prev, users: prev.users.length === all.length ? [] : all }));
  }

  function getAudienceSummary() {
    if (audienceTab === 'all') return { type: 'all', label: 'All Users', count: users.length };
    const parts = [];
    let count = 0;
    if (audience.depts.length) {
      const sel = departments.filter(d => audience.depts.includes(d.id));
      parts.push(`${sel.length} dept(s)`);
      count += sel.reduce((s, d) => s + (d.learnerCount || 0), 0);
    }
    if (audience.teams.length) {
      const allTeams = Object.values(teams).flat();
      const sel = allTeams.filter(t => audience.teams.includes(t.id));
      parts.push(`${sel.length} team(s)`);
      count += sel.reduce((s, t) => s + (t.memberCount || 0), 0);
    }
    if (audience.users.length) {
      parts.push(`${audience.users.length} individual(s)`);
      count += audience.users.length;
    }
    return { type: 'mixed', label: parts.length ? parts.join(' + ') : 'None selected', count };
  }

  function buildAudiences() {
    if (audienceTab === 'all') return [{ audienceType: 'all', targetId: null }];
    const result = [];
    if (audience.depts.length) result.push(...audience.depts.map(id => ({ audienceType: 'department', targetId: id })));
    if (audience.teams.length) result.push(...audience.teams.map(id => ({ audienceType: 'team', targetId: id })));
    if (audience.users.length) result.push(...audience.users.map(id => ({ audienceType: 'individual', targetId: id })));
    return result;
  }

  async function handleSend() {
    setSending(true);
    const formData = new FormData();
    formData.append('title', form.title);
    formData.append('body', form.body);
    formData.append('type', form.type);
    formData.append('priority', form.priority);
    formData.append('requiresAck', form.requiresAck);
    formData.append('allowReplies', form.allowReplies);
    formData.append('audiences', JSON.stringify(buildAudiences()));
    if (form.attachment) {
      formData.append('attachment', form.attachment);
    }
    try {
      await onSend(formData);
      onClose();
    } catch (err) {
      alert(err.message || 'Failed to send announcement');
    }
    setSending(false);
  }

  const filteredUsers = users.filter(u =>
    `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''} ${u.email || ''}`.toLowerCase().includes(userSearch.toLowerCase())
  );

  const canSend = form.title.trim() && form.body.trim() &&
    (audienceTab === 'all' || audience.depts.length > 0 || audience.teams.length > 0 || audience.users.length > 0);

  return (
    <DialogModal open={true} handleClose={onClose} title="New Announcement" maxWidth="md">
      <div className="flex flex-col" style={{ maxHeight: '75vh' }}>

        {/* Step Indicator */}
        <div className="flex border-b border-gray-100">
          {['Compose', 'Audience'].map((label, i) => (
            <button key={label} onClick={() => setStep(i + 1)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors border-b-2 ${step === i + 1 ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* Step 1: Compose */}
          {step === 1 && (
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Title <span className="text-red-500">*</span></label>
                <input type="text" value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Exam schedule update for NEET 2026"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                  <div className="flex gap-2">
                    {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                      <button key={key} type="button" onClick={() => setForm({ ...form, type: key })}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors ${form.type === key ? 'bg-purple-600 text-white border-purple-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Priority</label>
                  <div className="flex gap-2">
                    {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                      <button key={key} type="button" onClick={() => setForm({ ...form, priority: key })}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors ${form.priority === key ? 'bg-purple-600 text-white border-purple-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Message <span className="text-red-500">*</span></label>
                <RichEditor value={form.body} onChange={v => setForm({ ...form, body: v })} />
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.requiresAck}
                    onChange={e => setForm({ ...form, requiresAck: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400" />
                  <span className="text-sm text-gray-700">Requires acknowledgement</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.allowReplies}
                    onChange={e => setForm({ ...form, allowReplies: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400" />
                  <span className="text-sm text-gray-700">Allow replies</span>
                </label>
              </div>

              <div>
                <input type="file" ref={fileRef} className="hidden"
                  accept="image/*,.pdf,.xls,.xlsx,.csv,.doc,.docx"
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml', 'application/pdf', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                      if (!allowed.includes(file.type)) { e.target.value = ''; alert('Only images, PDF, Excel, CSV, and Word files are allowed.'); return; }
                      if (file.size > 25 * 1024 * 1024) { e.target.value = ''; alert('File size must be less than 25MB.'); return; }
                    }
                    setForm({ ...form, attachment: file || null });
                  }} />
                {form.attachment ? (
                  <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <Paperclip size={14} className="text-gray-400" />
                    <span className="flex-1 truncate">{form.attachment.name}</span>
                    <button onClick={() => setForm({ ...form, attachment: null })}
                      className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-purple-600 border border-dashed border-gray-300 hover:border-purple-400 rounded-lg px-3 py-2 transition-colors w-full">
                    <Paperclip size={14} /> Attach a file (optional)
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Audience */}
          {step === 2 && (
            <div className="flex flex-col h-full">
              <div className="flex border-b border-gray-100 bg-gray-50/60 px-4 pt-3 gap-1">
                {[
                  { key: 'all', label: 'All Users', icon: Globe },
                  { key: 'departments', label: 'Departments', icon: Building },
                  { key: 'teams', label: 'Teams', icon: Users },
                  { key: 'individuals', label: 'Individuals', icon: User },
                ].map(tab => (
                  <button key={tab.key} onClick={() => setAudienceTab(tab.key)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${audienceTab === tab.key ? 'border-purple-600 text-purple-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    <tab.icon size={13} /> {tab.label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-5">

                {audienceTab === 'all' && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">{users.length} user(s) will receive this announcement</p>
                    <div className="space-y-1">
                      {users.map(u => {
                        const fName = u.firstName || u.first_name || '';
                        const lName = u.lastName || u.last_name || '';
                        return (
                          <div key={u.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-200">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-xs font-semibold text-purple-700 flex-shrink-0">
                              {fName[0] || ''}{lName[0] || ''}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{fName} {lName}</p>
                              <p className="text-xs text-gray-400 truncate">{u.email}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {audienceTab === 'departments' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-gray-500">{audience.depts.length} of {departments.length} selected</p>
                      <button onClick={selectAllDepts}
                        className="text-xs text-purple-600 hover:text-purple-800 font-medium">
                        {audience.depts.length === departments.length ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    {departments.map(dept => (
                      <div key={dept.id}>
                        <div onClick={() => toggleDept(dept.id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${audience.depts.includes(dept.id) ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="checkbox" readOnly checked={audience.depts.includes(dept.id)}
                            className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400 pointer-events-none" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{dept.name}</p>
                            <p className="text-xs text-gray-400">{dept.teamCount || 0} teams · {dept.learnerCount || 0} learners</p>
                          </div>
                          <button type="button" onClick={e => { e.stopPropagation(); setExpandedDept(expandedDept === dept.id ? null : dept.id); loadTeams(dept.id); }}
                            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5">
                            Teams <ChevronDown size={12} className={`transition-transform ${expandedDept === dept.id ? 'rotate-180' : ''}`} />
                          </button>
                        </div>
                        {expandedDept === dept.id && teams[dept.id] && (
                          <div className="ml-8 mt-1 space-y-1">
                            {teams[dept.id].map(team => (
                              <div key={team.id} className="text-xs text-gray-500 flex items-center gap-2 py-1">
                                <Users size={10} className="text-gray-300" />
                                {team.name} ({team.memberCount || 0} members)
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {audienceTab === 'teams' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">{audience.teams.length} teams selected</p>
                      <button onClick={selectAllTeams}
                        className="text-xs text-purple-600 hover:text-purple-800 font-medium">
                        {audience.teams.length === Object.values(teams).flat().length ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    {departments.map(dept => (
                      <div key={dept.id}>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{dept.name}</p>
                        <div className="space-y-1">
                          {(teams[dept.id] || []).map(team => (
                            <div key={team.id} onClick={() => toggleTeam(team.id)}
                              className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${audience.teams.includes(team.id) ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                              <input type="checkbox" readOnly checked={audience.teams.includes(team.id)}
                                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400 pointer-events-none" />
                              <span className="text-sm text-gray-800 flex-1">{team.name}</span>
                              <span className="text-xs text-gray-400">{team.memberCount || 0} members</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {audienceTab === 'individuals' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="relative flex-1 mr-3">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)}
                          placeholder="Search users by name or email..."
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400" />
                      </div>
                      <button onClick={selectAllUsers}
                        className="text-xs text-purple-600 hover:text-purple-800 font-medium whitespace-nowrap">
                        {audience.users.length === users.length ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500">{audience.users.length} of {users.length} selected</p>
                    <div className="space-y-1">
                      {filteredUsers.map(u => {
                        const fName = u.firstName || u.first_name || '';
                        const lName = u.lastName || u.last_name || '';
                        const alreadyCovered = (u.departmentId && audience.depts.includes(u.departmentId))
                          || (u.teamId && audience.teams.includes(u.teamId));
                        return (
                          <div key={u.id} onClick={() => !alreadyCovered && toggleUser(u.id)}
                            className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${alreadyCovered ? 'border-green-200 bg-green-50 cursor-default' : audience.users.includes(u.id) ? 'border-purple-300 bg-purple-50 cursor-pointer' : 'border-gray-200 hover:bg-gray-50 cursor-pointer'}`}>
                            <input type="checkbox" readOnly checked={audience.users.includes(u.id) || alreadyCovered} disabled={alreadyCovered}
                              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400 pointer-events-none disabled:opacity-50" />
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-xs font-semibold text-purple-700 flex-shrink-0">
                              {fName[0] || ''}{lName[0] || ''}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{fName} {lName}</p>
                              <p className="text-xs text-gray-400 truncate">{u.email}</p>
                            </div>
                            {alreadyCovered && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium whitespace-nowrap">Already selected</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {(() => {
                const s = getAudienceSummary();
                return (
                  <div className="mx-5 mb-2 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-3">
                    <Check size={15} className="text-purple-600 flex-shrink-0" />
                    <div className="text-sm text-purple-800 min-w-0">
                      <span className="font-medium">Sending to: </span>
                      <span className="truncate">{s.label}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <div className="flex gap-2">
            {step === 2 && (
              <button onClick={() => setStep(1)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                ← Back
              </button>
            )}
            <button onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
          <div className="flex items-center gap-3">
            {step === 1 && (
              <button onClick={() => setStep(2)} disabled={!form.title.trim() || !form.body.trim()}
                className="px-5 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors">
                Next: Set Audience →
              </button>
            )}
            {step === 2 && (
              <button onClick={handleSend} disabled={!canSend || sending}
                className="px-5 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 transition-colors">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? 'Sending…' : 'Send Announcement'}
              </button>
            )}
          </div>
        </div>
      </div>
    </DialogModal>
  );
}

// ─── Detail Sidebar ───────────────────────────────────────────────────────────

function DetailSidebar({ announcement: ann, onClose }) {
  const [activeTab, setActiveTab] = useState('message');
  const [replyText, setReplyText] = useState('');
  const [replies, setReplies] = useState([]);
  const [threads, setThreads] = useState([]);
  const [threadMessages, setThreadMessages] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const currentUserId = useSelector((state) => state.userInfo?.id);

  useEffect(() => {
    if (ann?.allowReplies) loadThreads();
    loadUsers();
  }, [ann?.id]);

  async function loadThreads() {
    setLoadingReplies(true);
    const res = await adminServices.getAnnouncementThreads(ann.id);
    if (res.ok) setThreads(res.data || []);
    setLoadingReplies(false);
  }

  async function loadThreadMessages(userId) {
    const res = await adminServices.getAnnouncementThread(ann.id, userId);
    if (res.ok) setThreadMessages(res.data || []);
  }

  async function loadUsers() {
    setLoadingUsers(true);
    const res = await adminServices.getAnnouncementUserStatus(ann.id);
    if (res.ok) setUsers(res.data || []);
    setLoadingUsers(false);
  }

  async function handleSendReply() {
    if (!replyText.trim() || !selectedThread) return;
    const parentId = threadMessages.find(m => m.userId === selectedThread.userId)?.id || null;
    const res = await adminServices.createAnnouncementReply(ann.id, { message: replyText.trim(), parentReplyId: parentId });
    if (res.ok) { setThreadMessages(prev => [...prev, res.data]); setReplyText(''); loadThreads(); }
  }

  function openThread(thread) {
    setSelectedThread(thread);
    loadThreadMessages(thread.userId);
  }

  if (!ann) return null;
  const TypeIcon = TYPE_CONFIG[ann.type]?.icon || Megaphone;
  const readCount = ann.readCount || 0;
  const ackCount = ann.ackCount || 0;
  const replyCount = ann.replyCount || 0;

  return (
    <Modal title={ann.title} open={true} toggle={onClose}>
      <div className="flex flex-col" style={{ height: 'calc(100vh - 80px)' }}>

        <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex flex-wrap gap-1.5 mb-3">
            <TypeBadge type={ann.type} />
            <PriorityBadge priority={ann.priority} />
            <AudienceBadge audiences={ann.audiences} />
            {ann.requiresAck && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">Needs Ack</span>}
            {ann.allowReplies && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700">Replies On</span>}
          </div>
          <p className="text-xs text-gray-400 flex items-center gap-1"><CalendarDays size={11} /> Sent {fmtFull(ann.sentAt)}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/60 flex-shrink-0">
          <StatPill icon={Eye} value={readCount} label="Read" color="bg-blue-50 text-blue-700" />
          <StatPill icon={CheckCheck} value={ackCount} label="Acknowledged" color="bg-green-50 text-green-700" />
          <StatPill icon={MessageSquare} value={replyCount} label="Replies" color="bg-purple-50 text-purple-700" />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 flex-shrink-0">
          {[
            { key: 'message', label: 'Message', icon: BookOpen },
            { key: 'users', label: `Users (${users.length})`, icon: Users },
            ...(ann.allowReplies ? [{ key: 'replies', label: `Replies (${threads.length})`, icon: MessageSquare }] : []),
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 flex-1 py-2.5 text-xs font-medium transition-colors border-b-2 ${activeTab === tab.key ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              <tab.icon size={12} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'message' && (
            <div className="p-5">
              <div className="prose prose-sm max-w-none text-gray-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: ann.body }} />
              {ann.attachmentUrl && ann.attachmentName && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(ann.attachmentName) && (
                <div className="mt-4">
                  <img src={ann.attachmentUrl} alt={ann.attachmentName} className="w-full rounded-lg border border-gray-200" />
                </div>
              )}
              {ann.attachmentName && !(ann.attachmentUrl && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(ann.attachmentName)) && (
                <div className="mt-4 flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                  <Paperclip size={14} className="text-gray-400" /><a href={ann.attachmentUrl} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-purple-600 hover:underline">{ann.attachmentName}</a>
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="p-4">
              {loadingUsers ? (
                <div className="text-center py-8"><Loader2 size={20} className="animate-spin text-purple-400 mx-auto" /></div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-400">No users found.</div>
              ) : (
                <div className="space-y-2">
                  {users.filter(u => u.userId !== currentUserId).map(u => (
                    <div key={u.userId} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-xs font-semibold text-purple-700">
                        {(u.firstName || '')[0]}{(u.lastName || '')[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{u.firstName} {u.lastName}</p>
                        <p className="text-xs text-gray-400 truncate">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span title="Read" className={`w-5 h-5 rounded-full flex items-center justify-center ${u.hasRead ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-300'}`}>
                          <Eye size={10} />
                        </span>
                        {ann.requiresAck && (
                          <span title="Acknowledged" className={`w-5 h-5 rounded-full flex items-center justify-center ${u.hasAcknowledged ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-300'}`}>
                            <CheckCheck size={10} />
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'replies' && (
            <div className="flex flex-col h-full">
              {!selectedThread ? (
                /* Thread list - shows users who replied */
                <div className="flex-1 p-4 space-y-2">
                  {loadingReplies ? (
                    <div className="text-center py-8"><Loader2 size={20} className="animate-spin text-purple-400 mx-auto" /></div>
                  ) : threads.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-400">No replies yet.</div>
                  ) : (
                    threads.map(t => (
                      <div key={t.userId} onClick={() => openThread(t)}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition">
                        <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                          {(t.firstName || '')[0]}{(t.lastName || '')[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900 truncate">{t.firstName} {t.lastName}</p>
                            <span className="text-xs text-gray-400 flex-shrink-0">{fmt(t.lastSentAt)}</span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{t.email}</p>
                          <p className="text-xs text-gray-400 truncate mt-0.5">{t.lastMessage}</p>
                        </div>
                        <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <>
                  <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 bg-gray-50 flex-shrink-0">
                    <button onClick={() => setSelectedThread(null)} className="p-1 rounded hover:bg-gray-200"><ChevronDown size={14} className="rotate-90 text-gray-500" /></button>
                    <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700">
                      {(selectedThread.firstName || '')[0]}{(selectedThread.lastName || '')[0]}
                    </div>
                    <div><p className="text-sm font-medium text-gray-900">{selectedThread.firstName} {selectedThread.lastName}</p><p className="text-xs text-gray-400">{selectedThread.email}</p></div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {threadMessages.map(r => {
                      const isLearner = r.role === 'AUTH_USER' || r.role === 'MANAGER';
                      return (
                        <div key={r.id} className={`flex gap-2 ${!isLearner ? 'justify-end' : ''}`}>
                          {isLearner && (
                            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                              {(r.firstName || '')[0]}{(r.lastName || '')[0]}
                            </div>
                          )}
                          <div className={`max-w-[75%] rounded-xl px-3 py-2 ${!isLearner ? 'bg-purple-100 text-purple-900' : 'bg-gray-100 text-gray-800'}`}>
                            <p className="text-sm">{r.message}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{fmt(r.sentAt)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="p-3 border-t border-gray-100 flex gap-2 flex-shrink-0">
                    <input type="text" value={replyText} onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSendReply()}
                      placeholder={`Reply to ${selectedThread.firstName}…`}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
                    <button onClick={handleSendReply} disabled={!replyText.trim()}
                      className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40">
                      <Send size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [compose, setCompose] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterAudience, setFilterAudience] = useState('all');

  // Data for compose modal
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState({});
  const [users, setUsers] = useState([]);

  const userInfo = useSelector((state) => state.userInfo);

  // Load announcements on mount
  useEffect(() => {
    fetchAnnouncements();
    fetchDepartments();
    fetchUsers();
  }, []);

  async function fetchAnnouncements() {
    setLoading(true);
    const res = await adminServices.getAnnouncements();
    if (res.ok) setAnnouncements(res.data || []);
    setLoading(false);
  }

  async function fetchDepartments() {
    const res = await getDepartments();
    if (res.ok) {
      setDepartments(res.data || []);
      // Load teams for all departments
      const depts = res.data || [];
      for (const dept of depts) {
        loadTeamsForDept(dept.id);
      }
    }
  }

  async function loadTeamsForDept(deptId) {
    if (teams[deptId]) return;
    const res = await getTeams(deptId);
    if (res.ok) {
      setTeams(prev => ({ ...prev, [deptId]: res.data || [] }));
    }
  }

  async function fetchUsers() {
    const res = await tenantUserServices.getTenantUsers('', 0, 1000, '', '', 'ACTIVE');
    if (res.ok) {
      const data = res.data?.users || res.data || [];
      setUsers(data);
    }
  }

  async function handleSend(payload) {
    const res = await adminServices.createAnnouncement(payload);
    if (res.ok) {
      setAnnouncements(prev => [res.data, ...prev]);
    } else {
      throw new Error(res.data?.message || 'Failed to send announcement');
    }
  }

  const filtered = announcements.filter(a => {
    const matchSearch = (a.title || '').toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'all' || a.type === filterType;
    const audType = a.audiences?.[0]?.audienceType || 'all';
    const matchAudience = filterAudience === 'all' || audType === filterAudience;
    return matchSearch && matchType && matchAudience;
  });

  const totalSent = announcements.length;

  return (
    <Page title="Announcements">
      <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">

        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Announcements</h1>
            <p className="text-sm text-gray-500 mt-0.5">Broadcast messages to your teams, departments, or individuals</p>
          </div>
          <button onClick={() => setCompose(true)}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors shadow-sm">
            <Plus size={16} /> New Announcement
          </button>
        </div>


        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm px-4 py-3 mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search announcements…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400" />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={13} className="text-gray-400" />
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-400 text-gray-600">
              <option value="all">All types</option>
              <option value="urgent">Urgent</option>
              <option value="info">Info</option>
              <option value="general">General</option>
            </select>
            <select value={filterAudience} onChange={e => setFilterAudience(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-400 text-gray-600">
              <option value="all">All audiences</option>
              <option value="department">Department</option>
              <option value="team">Team</option>
              <option value="individual">Individual</option>
            </select>
            <button onClick={fetchAnnouncements} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin text-purple-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Bell size={32} className="text-gray-200 mb-3" />
              <p className="font-medium text-gray-500">No announcements found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or create a new one.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              <div className="hidden sm:grid grid-cols-12 px-5 py-2.5 bg-gray-50 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                <div className="col-span-4">Title</div>
                <div className="col-span-2">Audience</div>
                <div className="col-span-2">Read / Ack</div>
                <div className="col-span-2">Sent</div>
                <div className="col-span-2"></div>
              </div>

              {filtered.map(ann => {
                const TypeIcon = TYPE_CONFIG[ann.type]?.icon || Megaphone;
                const isSelected = selected?.id === ann.id;

                return (
                  <div key={ann.id}
                    onClick={() => setSelected(isSelected ? null : ann)}
                    className={`grid grid-cols-1 sm:grid-cols-12 gap-2 px-5 py-4 cursor-pointer transition-colors ${isSelected ? 'bg-purple-50 border-l-4 border-l-purple-500' : 'hover:bg-gray-50 border-l-4 border-l-transparent'}`}>

                    <div className="sm:col-span-4 flex items-start gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${ann.type === 'urgent' ? 'bg-red-100' : ann.type === 'info' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <TypeIcon size={15} className={TYPE_CONFIG[ann.type]?.iconColor || 'text-gray-400'} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{ann.title}</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          <TypeBadge type={ann.type} />
                          <PriorityBadge priority={ann.priority} />
                          {ann.requiresAck && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">Ack</span>}
                        </div>
                      </div>
                    </div>

                    <div className="sm:col-span-2 flex items-center">
                      <AudienceBadge audiences={ann.audiences} />
                    </div>

                    <div className="sm:col-span-2 flex items-center gap-2">
                      <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium">{ann.readCount || 0} read</span>
                      {ann.requiresAck && <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-medium">{ann.ackCount || 0} ack</span>}
                    </div>

                    <div className="sm:col-span-2 flex items-center text-xs text-gray-400">
                      <Clock size={11} className="mr-1" /> {fmt(ann.sentAt)}
                    </div>

                    <div className="sm:col-span-2 flex items-center justify-end">
                      <ChevronRight size={16} className={`transition-transform text-gray-300 ${isSelected ? 'rotate-90 text-purple-400' : ''}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detail Sidebar */}
      {selected && <DetailSidebar announcement={selected} onClose={() => setSelected(null)} />}

      {/* Compose Modal */}
      {compose && (
        <ComposeModal
          onClose={() => setCompose(false)}
          onSend={handleSend}
          departments={departments}
          teams={teams}
          users={users.filter(u => u.id !== userInfo?.id)}
          loadTeams={loadTeamsForDept}
        />
      )}
    </Page>
  );
}
